import React, { useState } from 'react';
import { Upload, Link as LinkIcon, Loader2, CheckCircle2, AlertCircle, Clock, FileText } from 'lucide-react';
import { getRunStatus, getRunTitle, timeAgo, TONE_CLASSES } from '../lib/runHelpers';
import { parseDriveFileUrl } from '../lib/driveValidation';

const STATUS_ICON = {
  pending: Clock,
  queued: Clock,
  transcribing: Loader2,
  mapping: Loader2,
  'creating-shorts': Loader2,
  done: CheckCircle2,
  error: AlertCircle,
};

export const PodcastCard = ({ run, onClick }) => {
  const [thumbError, setThumbError] = useState(false);
  const status = getRunStatus(run);
  const tone = TONE_CLASSES[status.tone] || TONE_CLASSES.amber;
  const StatusIcon = STATUS_ICON[status.key] || Clock;
  const SourceIcon = run.sourceFileType === 'upload' ? Upload : LinkIcon;
  const title = getRunTitle(run);

  // Drive-sourced runs: derive a thumbnail from Drive's public thumbnail endpoint.
  // Audio files / inaccessible files simply fail to load and fall back to the gradient header.
  const driveParsed =
    run.sourceFileType !== 'upload' && run.sourceFileUrl ? parseDriveFileUrl(run.sourceFileUrl) : null;
  const thumbnailUrl =
    driveParsed?.valid && !thumbError
      ? `https://drive.google.com/thumbnail?id=${driveParsed.fileId}&sz=w400`
      : null;

  const timing =
    typeof run.n8nExecutionDurationMinutes === 'number'
      ? `${run.n8nExecutionDurationMinutes.toFixed(1)} min`
      : timeAgo(run.createdAt);

  return (
    <div
      onClick={onClick}
      className="group relative bg-brand-surface rounded-xl overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-orange-500/10 border border-brand-surface-3 hover:border-orange-500/50"
    >
      {/* Header — compact status strip */}
      <div className={`relative h-20 sm:h-24 bg-gradient-to-br ${tone.gradient} overflow-hidden`}>
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt=""
            loading="lazy"
            onError={() => setThumbError(true)}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-brand-bg/30" />
        {!thumbnailUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <StatusIcon
              className={`w-9 h-9 sm:w-10 sm:h-10 ${tone.text} ${status.spinning ? 'animate-spin' : ''}`}
              strokeWidth={1.5}
            />
          </div>
        )}

        {/* Status pill */}
        <div className={`absolute top-2 left-2 px-1.5 py-0.5 ${tone.bg} ${tone.border} border backdrop-blur-sm rounded-md flex items-center gap-1`}>
          <span className={`w-1 h-1 rounded-full ${tone.dot} ${status.spinning ? 'animate-pulse' : ''}`} />
          <span className={`text-[10px] font-medium ${tone.text}`}>{status.label}</span>
        </div>

        {/* Source badge */}
        <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-brand-bg/80 backdrop-blur-sm rounded-md flex items-center gap-1">
          <SourceIcon className="w-2.5 h-2.5 text-orange-400" />
          <span className="text-[10px] text-white font-medium">
            {run.sourceFileType === 'upload' ? 'Upload' : 'Drive'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        <h3
          dir="auto"
          className="text-sm font-semibold text-white mb-1.5 line-clamp-2 group-hover:text-orange-400 transition-colors duration-200"
        >
          {title}
        </h3>
        {run.errorMessage && (
          <p
            dir="auto"
            className="text-xs text-red-400 mb-2 line-clamp-1"
          >
            {run.errorMessage}
          </p>
        )}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-1 min-w-0">
            <FileText className="w-3 h-3 flex-shrink-0" />
            <span dir="auto" className="truncate">
              {run.originalFileName || 'Drive link'}
            </span>
          </div>
          <span className="flex-shrink-0 ml-2">{timing}</span>
        </div>
      </div>
    </div>
  );
};
